import React, { useMemo } from "react";
import ReactECharts from "echarts-for-react";

export default function TimeSeriesChart({
  data,
  statsData,
  selectedVar,
  variableOptions,
}) {
  const variableConfig = variableOptions.find(
    (v) => v.value === selectedVar
  );

  // --------------------------------------------------
  // Timestamp helper
  // --------------------------------------------------
  const getTimestamp = (d) => {
    try {
      if (d.mtSampTime) {
        return new Date(d.mtSampTime).getTime();
      }

      if (d.DateTime) {
        return new Date(d.DateTime).getTime();
      }

      if (d.date) {
        const dateStr = String(d.date);

        // YYYYMMDD
        if (/^\d{8}$/.test(dateStr)) {
          return new Date(
            `${dateStr.slice(0, 4)}-${dateStr.slice(4, 6)}-${dateStr.slice(
              6,
              8
            )}`
          ).getTime();
        }

        return new Date(dateStr).getTime();
      }

      return NaN;
    } catch (error) {
      return NaN;
    }
  };

  // --------------------------------------------------
  // Normalize incoming data
  // --------------------------------------------------
  const processedData = useMemo(() => {
    return data.map((d) => ({
      ...d,
      timestamp: getTimestamp(d),
    }));
  }, [data]);

  // --------------------------------------------------
  // Main series
  // --------------------------------------------------
  const seriesData = useMemo(() => {
    console.log("selectedVar", selectedVar);
    console.log("first record", data?.[0]);
    return processedData
      .map((d) => {
        const value = parseFloat(
          d[selectedVar] ?? d[variableConfig?.wims_name]
        );

        if (isNaN(value) || isNaN(d.timestamp)) {
          return null;
        }

        return [d.timestamp, value];
      })
      .filter(Boolean);
  }, [processedData, selectedVar, variableConfig]);

  // --------------------------------------------------
  // Climate lookup
  // --------------------------------------------------
  const statsLookup = useMemo(() => {
    return statsData.reduce((acc, row) => {
      acc[row.Date] = row;
      return acc;
    }, {});
  }, [statsData]);

  const buildStatsSeries = (field) => {
    return processedData
      .map((d) => {
        if (isNaN(d.timestamp)) return null;

        const time = new Date(d.timestamp);

        const mmdd =
          String(time.getMonth() + 1).padStart(2, "0") +
          "-" +
          String(time.getDate()).padStart(2, "0");

        const stat = statsLookup[mmdd];

        if (!stat || stat[field] == null) {
          return null;
        }

        return [
          d.timestamp,
          parseFloat(stat[field]),
        ];
      })
      .filter(Boolean);
  };

  const maxSeriesData = useMemo(
    () => buildStatsSeries("Max"),
    [processedData, statsLookup]
  );

  const minSeriesData = useMemo(
    () => buildStatsSeries("Min"),
    [processedData, statsLookup]
  );

  const seriesData90 = useMemo(
    () => buildStatsSeries("90th Percentile"),
    [processedData, statsLookup]
  );

  // --------------------------------------------------
  // Chart options
  // --------------------------------------------------
  const option = useMemo(
    () => ({
      backgroundColor: "transparent",

      animation: true,

      grid: {
        top: 35,
        left: 55,
        right: 20,
        bottom: 45,
      },

      tooltip: {
        trigger: "axis",

        backgroundColor: "rgba(20,20,20,0.97)",
        borderColor: "#404040",
        borderWidth: 1,
        padding: 12,

        textStyle: {
          color: "#f5f5f5",
          fontSize: 13,
        },

        axisPointer: {
          type: "cross",
          lineStyle: {
            color: "#888",
            type: "dashed",
          },
        },

        formatter: (params) => {
          if (!params?.length) return "";

          const time = params[0].value[0];

          const date = new Date(time).toLocaleString(undefined, {
            year: "numeric",
            month: "short",
            day: "numeric",
            hour: "numeric",
            minute: "2-digit",
          });

          const rows = params
            .filter((p) => p.value?.[1] != null)
            .map(
              (p) => `
              <div style="
                  display:flex;
                  justify-content:space-between;
                  gap:18px;
                  margin:3px 0;
                  min-width:180px;
              ">
                  <span>
                  <span style="
                      display:inline-block;
                      width:10px;
                      height:10px;
                      border-radius:50%;
                      background:${p.color};
                      margin-right:6px;
                  "></span>
                  ${p.seriesName}
                  </span>

                  <span style="
                    font-weight:600;
                    color:white;
                  ">
                    ${Number(p.value[1]).toFixed(2)}
                  </span>
              </div>
            `
            )
            .join("");

          return `
            <div style="min-width:220px;">
              <div style="
                font-size:12px;
                color:#9aa4b2;
                margin-bottom:8px;
                padding-bottom:6px;
                border-bottom:1px solid rgba(255,255,255,0.08);
              ">
                ${date}
              </div>

              ${rows}
            </div>
          `;
        },
      },

      xAxis: {
        type: "time",
        name: "Time",

        axisLabel: {
          color: "#bdbdbd",
          hideOverlap: true,

          formatter: (value) => {
            const d = new Date(value);
            const mm = String(d.getMonth() + 1).padStart(2, "0");
            const dd = String(d.getDate()).padStart(2, "0");
            return `${mm}-${dd}`;
          },
        },

        splitLine: {
          show: true,
          lineStyle: {
            color: "rgba(255,255,255,0.05)",
            type: "dashed",
          },
        },

        axisTick: {
          show: false,
        },

        axisLine: {
          lineStyle: {
            color: "#666",
          },
        },
      },

      yAxis: {
        type: "value",

        scale: true,

        name: selectedVar,

        nameTextStyle: {
          color: "#bdbdbd",
          padding: [0, 0, 0, 10],
        },

        axisLine: {
          show: false,
        },

        axisLabel: {
          color: "#bdbdbd",
        },

        splitLine: {
          lineStyle: {
            color: "rgba(255,255,255,0.06)",
          },
        },
      },

      legend: {
        top: 5,
        right: 10,
        textStyle: {
          color: "#cfcfcf",
        },
      },

      dataZoom: [
        {
          type: "inside",
          throttle: 50,
        },
        {
          type: "slider",
          height: 18,
          bottom: 5,
          borderColor: "#333",
          backgroundColor: "#1a1a1a",
          fillerColor: "rgba(77,163,255,0.25)",
          handleStyle: {
            color: "#4DA3FF",
            borderColor: "#7fc1ff",
          },
          textStyle: {
            color: "#999",
          },
        },
      ],

      series: [
        {
          name: selectedVar,
          type: "line",
          smooth: 0.15,
          showSymbol: false,
          sampling: "lttb",

          emphasis: {
            focus: "series",
          },

          lineStyle: {
            width: 2.5,
            color: "#92c7ff",
            shadowBlur: 5,
            shadowColor: "#085eff",
          },

          areaStyle: {
            color: {
              type: "linear",
              x: 0,
              y: 0,
              x2: 0,
              y2: 1,
              colorStops: [
                {
                  offset: 0,
                  color: "rgba(77,163,255,0.35)",
                },
                {
                  offset: 1,
                  color: "rgba(77,163,255,0.02)",
                },
              ],
            },
          },

          data: seriesData,
        },

        {
          name: "Historical Max",
          type: "line",
          showSymbol: false,
          smooth: 0.15,
          lineStyle: {
            color: "#ff6b6b",
            width: 1.5,
            type: "dashed",
            opacity: 0.7,
          },
          data: maxSeriesData,
        },

        {
          name: "Historical Min",
          type: "line",
          showSymbol: false,
          smooth: 0.15,
          lineStyle: {
            color: "#4ecdc4",
            width: 1.5,
            type: "dashed",
            opacity: 0.7,
          },
          data: minSeriesData,
        },

        {
          name: "90th Percentile",
          type: "line",
          showSymbol: false,
          smooth: 0.15,
          lineStyle: {
            color: "#cdc54e",
            width: 1.5,
            type: "dashed",
            opacity: 0.7,
          },
          data: seriesData90,
        },
      ],
    }),
    [
      selectedVar,
      seriesData,
      maxSeriesData,
      minSeriesData,
      seriesData90,
    ]
  );

  return (
    <ReactECharts
      option={option}
      style={{
        height: "300px",
        width: "100%",
      }}
      opts={{
        renderer: "canvas",
      }}
    />
  );
}